// eslint-disable-next-line no-restricted-imports
import styled from '@emotion/styled';
import type { StoreSpecial as V3StoreSpecial } from '@iheartjane/data-access';
import { ALL_STORE_SPECIALS_OPTS } from '@iheartjane/data-access';
import { useStoreSpecials } from '@iheartjane/data-access/react-query';
import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import {
  ArrowLeftIcon,
  BagIcon,
  Box,
  Button,
  Flex,
  Link,
  List,
  Typography,
  useToast,
} from '@iheartjane/reefer';
import type { CartBrandDiscount } from '@iheartjane/types';
import capitalize from 'lodash-es/capitalize';
import { darken } from 'polished';
import { useCallback, useMemo } from 'react';
import { Outlet, useNavigate } from 'react-router';

import { useCartTopperRow } from '@jane/dm/internal';
import {
  useAddCartProduct,
  useGetStoreSpecials,
} from '@jane/shared-ecomm/data-access';
import { cartSize } from '@jane/shared-ecomm/util';
import { useGetPosCart } from '@jane/shared/data-access';
import { useNavigateBack } from '@jane/shared/hooks';
/* eslint-disable-next-line no-restricted-imports */
import type { MenuProduct as IoTsMenuProduct } from '@jane/shared/models';
import type {
  BrandSpecial,
  PendingCartProduct,
  StoreSpecial,
  Cart as ZodCart,
} from '@jane/shared/types';
import {
  availableWeightsForMenuProduct,
  calculateDiscount,
  calculateDiscountPercentage,
  generateCartLimitReport,
  getBundlesInProgress,
  isDiscountSupported,
} from '@jane/shared/util';

import {
  Alert,
  CartLimitPolicyAlert,
  CartRowActions,
  CartToppers,
  CartTotals,
  IconButton,
  JaneGoldCallout,
  ProductAddedToast,
  ProductRow,
} from '../../components';
import type { RootLoaderType } from '../../data-access';
import {
  useGetCart,
  useGetKiosk,
  useKioskUser,
  useUpdateCartProduct,
} from '../../data-access';
import {
  useKioskSpacing,
  useShouldKioskShowGold,
  useTypedRouteLoaderData,
} from '../../hooks';
import { useBrandSpecials, useProductTracking } from '../../providers';
import type { AddToBagParams, ProductParams } from '../../types';
import {
  getUsername,
  isAcceptingOrders,
  isSupportedStoreSpecial,
  nextAvailableSchedule,
  trackAddedProductToBag,
  trackCheckoutInitiated,
} from '../../util';
import { trackCashBackPromptTap } from '../../util/lib/tracking/track';
import styles from './Cart.module.css';

const ContinueShoppingButton = styled(Button)(() => ({
  paddingLeft: 8,
  paddingRight: 8,
}));

const EmptyBag = styled(Flex)(({ theme }) => ({
  alignItems: 'center',
  backgroundColor: theme.colors.grays.ultralight,
  borderRadius: 24,
  flexDirection: 'column',
  grow: 1,
  height: '100%',
  justifyContent: 'center',
  paddingBottom: 12,
  paddingTop: 12,
}));

const OuterBox = styled(Flex)(({ theme }) => ({
  backgroundColor: theme.colors.grays.white,
  flexDirection: 'column',
  minHeight: '100vh',
  width: '100vw',
}));

const StyledButton = styled(Button)(({ theme }) => {
  const { getKioskSpacing } = useKioskSpacing();
  const paddingY = getKioskSpacing(4, 2);
  const color = darken(0.05, theme.colors.grays.ultralight);

  return {
    border: 'unset',
    backgroundColor: color,
    borderRadius: 32,
    height: 'auto',
    padding: `${paddingY}px 12px ${paddingY}px 12px`,
  };
});

export const Cart = () => {
  const showGold = useShouldKioskShowGold({});
  const {
    kiosk: initialKiosk,
    kioskId,
    store,
    storeId,
  } = useTypedRouteLoaderData<RootLoaderType>('root');
  const {
    inlineAds: { inlineAdIds },
    cartToppers: { cartTopperIds, addCartTopperId },
  } = useProductTracking();

  const { data: kiosk } = useGetKiosk({
    initialData: initialKiosk,
    kioskId,
  });
  const { data: cart = {}, isFetching: isCartFetching } = useGetCart();
  const { data: posCart, isFetching: isPosCartFetching } = useGetPosCart(
    cart.uuid
  );

  const isCartTotalsFetching = isCartFetching || isPosCartFetching;

  const { data: userData } = useKioskUser();

  const userSignedIn = !!userData;
  const userName = useMemo(() => getUsername(userData), [userData]);

  const useV3 = useFlag(FLAGS.c3UseV3StoreSpecials);
  const { data: v1Specials = [] } = useGetStoreSpecials(storeId, {
    enabled: !useV3 && !!storeId,
  });
  const v3SpecialsQuery = useStoreSpecials({
    ...ALL_STORE_SPECIALS_OPTS,
    enabled: !!useV3 && !!storeId,
    storeId,
  });
  const specials = useMemo<(StoreSpecial | V3StoreSpecial)[]>(() => {
    if (useV3) {
      return v3SpecialsQuery.data?.pages.flatMap((page) => page.specials) ?? [];
    }
    return v1Specials;
  }, [useV3, v3SpecialsQuery.data, v1Specials]);
  const { brandSpecials } = useBrandSpecials();
  const { mutate: updateProduct } = useUpdateCartProduct({});
  const { mutate: addCartProduct } = useAddCartProduct({});

  const navigate = useNavigate();
  const goBack = useNavigateBack(`stores/${storeId}/menu`);
  const toast = useToast();

  const cartProductIds = useMemo(
    () => cart?.products?.map(({ product_id }) => product_id) ?? [],
    [cart?.products]
  );

  const { isLoading, instance } = useCartTopperRow({
    cartProductIds,
    searchAttributes: ['*'],
    storeId,
  });

  const cartProductsCount = cartSize(cart);
  const cartLimitReport = generateCartLimitReport({ cart, store });
  const supportedStoreSpecials = specials.filter((special) =>
    isSupportedStoreSpecial(special, true)
  );

  const bundlesInProgress = getBundlesInProgress({
    brandSpecials,
    cart: cart as ZodCart,
  });
  const isCartEmpty = useMemo(
    () => cartProductsCount === 0,
    [cartProductsCount]
  );

  const isStoreAcceptingOrders = isAcceptingOrders(store);
  const nextSchedule = nextAvailableSchedule(store);

  const { defaultSpacing, getKioskSpacing, isIpad } = useKioskSpacing();

  const onRemoveProduct = useCallback(
    (productId: PendingCartProduct['product_id']) => {
      navigate(`/stores/${storeId}/cart/remove/${productId}`);
    },
    [navigate, storeId]
  );

  const onUpdateProduct = useCallback(
    (product: PendingCartProduct, quantity: string) => {
      updateProduct({
        cart_uuid: cart.uuid,
        product: { ...product, count: parseInt(quantity) } as ProductParams,
      });

      const originalPrice = product[`price_${product.price_id}`];
      const discountedPrice = isDiscountSupported(
        product as IoTsMenuProduct,
        product.price_id,
        supportedStoreSpecials
      )
        ? product[`discounted_price_${product.price_id}`]
        : undefined;

      const isTopper = cartTopperIds.includes(product.product_id);
      const isInlineAd = inlineAdIds.includes(product.product_id);

      trackAddedProductToBag({
        cart,
        dmType: isTopper ? ['bag'] : isInlineAd ? ['in-line'] : null,
        productDetails: {
          product,
          productPercentageOff: discountedPrice
            ? calculateDiscountPercentage(
                Number(originalPrice) * Number(quantity),
                Number(discountedPrice) * Number(quantity)
              )
            : null,
          productPriceId: product.price_id,
          productQuantity: Number(quantity) - product.count,
          productSavingsAmount: discountedPrice
            ? calculateDiscount(
                Number(originalPrice) * Number(quantity),
                Number(discountedPrice) * Number(quantity)
              )
            : null,
          productSpecialId: product.special_id,
        },
        quickAdd: false,
        source: ['bag'],
        store,
      });
    },
    [cart, store, updateProduct, cartTopperIds, inlineAdIds, posCart]
  );

  const onAddCartTopper = useCallback(
    ({ priceId, product, productInstance }: AddToBagParams) => {
      const weights = availableWeightsForMenuProduct(
        product as IoTsMenuProduct
      );

      if (weights.length > 1) {
        productInstance.click();
        navigate(
          `/stores/${storeId}/cart/select-addon-weight/${product.product_id}?priceId=${priceId}&topper=true`
        );
      } else {
        productInstance.click({ isAddToCart: true });
        addCartTopperId(product.product_id);
        addCartProduct({
          app_mode: 'kiosk',
          app_mode_name: 'kiosk',
          cart_uuid: cart.uuid,
          product: {
            count: 1,
            price_id: priceId,
            product_id: product.product_id,
            recommended: false,
          },
          store_id: storeId,
          user_agent: window.navigator.userAgent,
        });

        toast.add({
          label: <ProductAddedToast productCount={1} storeId={storeId} />,
        });
      }
    },
    [addCartProduct, addCartTopperId, cart.uuid, navigate, storeId, toast]
  );

  const onProductClick = useCallback(
    (product: PendingCartProduct) => {
      navigate(
        `/stores/${storeId}/cart/products/${product.product_id}?priceId=${product.price_id}&pdpDepth=1`
      );
    },
    [navigate, storeId]
  );

  const renderCartProduct = useCallback(
    (product: PendingCartProduct) => {
      const janeGoldTagLabel = showGold
        ? product.brand_discount_title || product.brand_discount_label
        : null;

      return (
        <List.Item
          disableHover
          key={`${product.product_id}-${product.price_id}`}
          px={0}
          py={0}
        >
          <ProductRow
            actions={
              <CartRowActions
                onChangeQuantity={onUpdateProduct}
                onRemove={onRemoveProduct}
                product={product}
              />
            }
            janeGoldTagLabel={janeGoldTagLabel}
            product={product}
            priceId={product.price_id}
            onProductImageClick={onProductClick}
            showRating={false}
          />
        </List.Item>
      );
    },
    [onProductClick, onRemoveProduct, onUpdateProduct]
  );

  const onContinueShopping = useCallback(() => {
    navigate(`/stores/${storeId}/menu`);
  }, [navigate, storeId]);

  const onCheckout = useCallback(() => {
    trackCheckoutInitiated({
      cart,
      kiosk,
      specials,
      store,
    });
    navigate(
      `/stores/${storeId}/cart/checkout${
        kiosk?.checkout_on_separate_device ? `-on-device` : ''
      }`
    );
  }, [cart, navigate, specials, store, storeId, kiosk]);

  const onBrandSpecialClick = (brandSpecial: BrandSpecial) => {
    trackCashBackPromptTap({ brandSpecial, source: ['Cart'] });

    navigate(`/stores/${store.id}/menu/jane-gold/${brandSpecial.id}`);
  };

  return (
    <OuterBox pb={getKioskSpacing(48)}>
      <Flex
        flexDirection="column"
        pt={userSignedIn ? getKioskSpacing(24, 8) : getKioskSpacing(72, 48)}
        mx={getKioskSpacing(64, 48)}
      >
        {userSignedIn && (
          <StyledButton
            variant="tertiary"
            size="small"
            label={
              userName?.includes('privaterelay.appleid.com')
                ? "Didn't sign in? Logout"
                : `Not ${userName}? Logout`
            }
            ml="auto"
            mb={getKioskSpacing(16, 12)}
            onClick={() => navigate(`/stores/${store.id}/cart/logout`)}
          />
        )}
        <Flex
          alignItems="center"
          flexDirection="row"
          pb={getKioskSpacing(40, 32)}
        >
          <IconButton
            ariaLabel="navigate back"
            onClick={() => goBack()}
            icon={
              <ArrowLeftIcon color="grays-black" size={isIpad ? 'lg' : 'xl'} />
            }
            style={{ boxShadow: '2px 4px 14px 0 rgba(0, 0, 0, 0.08)' }}
          />
          <Typography
            ml={defaultSpacing}
            variant={isIpad ? 'title' : 'huge'}
            branded
          >
            Your bag
          </Typography>
        </Flex>

        <Box
          className={styles['boxShadow']}
          borderRadius="lg"
          p={getKioskSpacing(40, 32)}
          mb={getKioskSpacing(24, 16)}
        >
          <CartTotals
            cart={cart}
            posCart={posCart}
            isLoading={isCartTotalsFetching}
          />
        </Box>

        {showGold && !!cart.brand_discounts_total_with_fee && (
          <JaneGoldCallout
            variant="cart"
            brandDiscounts={cart.brand_discounts as CartBrandDiscount[]}
            brandDiscountsServiceFeeTotal={
              cart.brand_discounts_service_fee_total
            }
            brandDiscountsTotal={cart.brand_discounts_total ?? 0}
            brandDiscountsTotalWithFees={cart.brand_discounts_total_with_fee}
          />
        )}
        <Flex
          flexDirection="row"
          gap={getKioskSpacing(24, 16)}
          mb={getKioskSpacing(48, 24)}
        >
          <ContinueShoppingButton
            ariaLabel="continue shopping"
            full
            label={
              <Typography
                color="text-main"
                variant={`${isIpad ? 'body' : 'section'}-bold`}
                as="div"
                branded
              >
                Continue shopping
              </Typography>
            }
            onClick={onContinueShopping}
            variant="tertiary"
            size={isIpad ? 'small' : 'large'}
          />
          <Button
            disabled={
              !cart?.uuid || cartLimitReport.over || !isStoreAcceptingOrders
            }
            full
            label={
              <Typography
                color="text-info"
                variant={`${isIpad ? 'body' : 'section'}-bold`}
                as="div"
                branded
              >
                Checkout
              </Typography>
            }
            onClick={onCheckout}
            variant="primary"
            size={isIpad ? 'small' : 'large'}
          />
        </Flex>
      </Flex>

      <Flex
        flexDirection="column"
        grow={1}
        mx={getKioskSpacing(64, 48)}
        height="100%"
        mb={isIpad ? 8 : 'auto'}
      >
        {!isStoreAcceptingOrders && (
          <Alert
            label={`Bummer! We're closed for the day. ${
              nextSchedule && nextSchedule.day && nextSchedule.period.from
                ? `You can place an order again on ${capitalize(
                    nextSchedule.day
                  )}.`
                : ''
            }`}
          />
        )}
        {!isCartEmpty ? (
          <>
            {isStoreAcceptingOrders && (
              <CartLimitPolicyAlert report={cartLimitReport} />
            )}

            <Typography variant={isIpad ? 'body-bold' : 'header-bold'} as="h2">
              Your items
            </Typography>
            {bundlesInProgress.length > 0 && (
              <JaneGoldCallout
                variant="bundleProgress"
                bundlesInProgress={bundlesInProgress}
                cart={cart as ZodCart}
                onClick={(brandSpecial) =>
                  onBrandSpecialClick(brandSpecial as BrandSpecial)
                }
              />
            )}
            <Box
              borderRadius="lg"
              border="transparent-black-five"
              mb={getKioskSpacing(48, 24)}
              mt={getKioskSpacing(24, 16)}
              pt={getKioskSpacing(48, 32)}
              pl={getKioskSpacing(48, 32)}
              pr={getKioskSpacing(24, 12)}
            >
              <List label="cart-products-list">
                {(cart.products ?? []).map(renderCartProduct)}
              </List>
            </Box>
          </>
        ) : (
          <EmptyBag
            alignItems="center"
            grow={1}
            height="100%"
            justifyContent="center"
            flexDirection="column"
          >
            <BagIcon mb={32} size="xxxl" />
            <Typography variant="body-bold">
              Your bag is empty. Continue shopping to add products.
            </Typography>
            <Link
              typography="body"
              mt={16}
              to={`/stores/${storeId}/menu`}
              ariaLabel="shop all products"
            >
              Shop now
            </Link>
          </EmptyBag>
        )}
      </Flex>

      {!isCartEmpty && (
        <Flex flexDirection="column" ml={getKioskSpacing(64, 48)}>
          <CartToppers
            onAddCartTopper={onAddCartTopper}
            isLoading={isLoading}
            row={instance}
          />
        </Flex>
      )}
      <Outlet />
    </OuterBox>
  );
};
